import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { neveroverpayApi } from './neveroverpayApi';
import BrandMark from './BrandMark';
import NeverOverpayWelcome, { flushWelcomeConsent, markWelcomeAccepted } from './NeverOverpayWelcome';
import { GmailLogo } from './GmailConnectCard';
import { Glow } from './npUi';
import './neveroverpay.css';
import './neveroverpay-ui.css';

const API = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
  ? process.env.REACT_APP_BACKEND_URL
  : '';

export default function NeverOverpayAuth({ setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requested = (params.get('mode') || '').trim().toLowerCase();
  const [mode, setMode] = useState(
    requested === 'register' || requested === 'signup' ? 'register' : 'login',
  );
  const [showEmailForm, setShowEmailForm] = useState(params.get('form') === '1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [googleReady, setGoogleReady] = useState(false);

  const nextPath = React.useMemo(() => {
    const raw = (params.get('next') || '').trim();
    if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('://')) return '/onboarding';
    if (raw.startsWith('/app') || raw.startsWith('/cases') || raw.startsWith('/ops') || raw.startsWith('/ledger') || raw.startsWith('/recover') || raw.startsWith('/admin') || raw.startsWith('/gmail') || raw.startsWith('/onboarding')) {
      return raw.startsWith('/recover') ? '/app' : raw;
    }
    return '/onboarding';
  }, [params]);

  React.useEffect(() => {
    fetch(`${API}/api/auth/google/status`)
      .then((res) => (res.ok ? res.json() : null))
      .then((row) => setGoogleReady(Boolean(row?.configured)))
      .catch(() => setGoogleReady(false));
  }, []);

  React.useEffect(() => {
    if (requested === 'register' || requested === 'signup') setMode('register');
    if (requested === 'login' || requested === 'signin') setMode('login');
    setShowEmailForm(params.get('form') === '1');
  }, [requested, params]);

  const onChange = (event) => {
    setForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  function switchMode(next, { emailForm = false } = {}) {
    setError('');
    setMode(next);
    setShowEmailForm(Boolean(emailForm));
    const nextParams = new URLSearchParams(location.search);
    nextParams.set('mode', next === 'register' ? 'register' : 'login');
    if (emailForm) nextParams.set('form', '1');
    else nextParams.delete('form');
    nextParams.delete('_v');
    navigate({ pathname: '/auth', search: `?${nextParams.toString()}` }, { replace: true });
  }

  const googleHref = `${API}/api/auth/google/login?next=${encodeURIComponent(mode === 'register' ? '/onboarding' : nextPath)}`;
  const googleError = params.get('google_error') === '1';
  const showWelcome = mode === 'register' && !showEmailForm;
  const panelKey = showWelcome ? 'welcome' : mode;

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (mode === 'register') markWelcomeAccepted();
    setLoading(true);
    const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
    const payload = mode === 'login'
      ? { email: form.email, password: form.password }
      : { email: form.email, password: form.password, name: form.name, accept_terms: true };
    try {
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        setError(typeof detail === 'string' ? detail : 'Could not sign in');
        return;
      }
      localStorage.setItem('token', data.token);
      if (setUser) setUser(data.user);
      if (mode === 'register') {
        await flushWelcomeConsent((kind) => neveroverpayApi.grantConsent(kind));
      }
      navigate(mode === 'register' ? '/onboarding' : nextPath, { replace: true });
    } catch (err) {
      setError('Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="np-root np-grid-bg np-auth-page" data-testid="neveroverpay-auth">
      <Glow />
      <div className="np-auth-shell">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
          <BrandMark to="/" className="np-auth-mark" kicker={mode === 'login' ? 'Sign in' : 'Sign up'} />
        </motion.div>

        <div className="np-card np-auth-card">
          <div className="np-tabs np-auth-tabs" role="tablist" aria-label="Account">
            <button type="button" className={`np-tab${mode === 'login' ? ' on' : ''}`} onClick={() => switchMode('login')}>
              {mode === 'login' ? <motion.span className="np-tab-pill" layoutId="np-auth-tab" /> : null}
              <span className="np-tab-label">Sign in</span>
            </button>
            <button type="button" className={`np-tab${mode === 'register' ? ' on' : ''}`} onClick={() => switchMode('register')}>
              {mode === 'register' ? <motion.span className="np-tab-pill" layoutId="np-auth-tab" /> : null}
              <span className="np-tab-label">Sign up</span>
            </button>
          </div>

          {googleError ? (
            <div className="np-error np-auth-error">Google sign-in was canceled or failed. Try again, or use email.</div>
          ) : null}

          <div key={panelKey} className="np-auth-panel">
            {showWelcome ? (
              <NeverOverpayWelcome
                googleReady={googleReady}
                googleHref={googleHref}
                onEmail={() => switchMode('register', { emailForm: true })}
              />
            ) : (
              <>
                <h1 className="np-auth-title">
                  {mode === 'login' ? 'Sign in to recover money' : 'Continue with email'}
                </h1>
                <p className="np-muted np-auth-lead">
                  {mode === 'login'
                    ? 'You stay in control. We never send, cancel, change, or accept anything without your approval.'
                    : 'Create your account. We still ask before we contact a merchant.'}
                </p>

                {googleReady ? (
                  <a
                    className="np-btn gmail np-btn-lg np-btn-icon np-auth-full"
                    href={googleHref}
                    onClick={() => { if (mode === 'register') markWelcomeAccepted(); }}
                  >
                    <GmailLogo size={18} /> Continue with Google
                  </a>
                ) : (
                  <p className="np-muted np-auth-hint">Google sign-in is not configured yet. Use email, or paste Client ID and secret in Admin.</p>
                )}

                <div className="np-auth-or" role="separator"><span>or</span></div>

                <form className="np-auth-form" onSubmit={submit}>
                  {mode === 'register' ? (
                    <label className="np-auth-field">
                      Name
                      <input className="np-input" name="name" value={form.name} onChange={onChange} required autoComplete="name" />
                    </label>
                  ) : null}
                  <label className="np-auth-field">
                    Email
                    <input className="np-input" type="email" name="email" value={form.email} onChange={onChange} required autoComplete="email" />
                  </label>
                  <label className="np-auth-field">
                    Password
                    <input className="np-input" type="password" name="password" value={form.password} onChange={onChange} required minLength={8} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} />
                  </label>
                  {error ? <div className="np-error">{typeof error === 'string' ? error : 'Authentication failed'}</div> : null}
                  <button type="submit" className="np-btn np-btn-lg np-auth-full" disabled={loading}>
                    {loading ? 'Working…' : (mode === 'login' ? 'Continue with email' : 'Create account')}
                  </button>
                </form>

                <p className="np-muted np-auth-switch">
                  {mode === 'login' ? (
                    <>No account? <button type="button" className="np-auth-text" onClick={() => switchMode('register')}>Create one</button></>
                  ) : (
                    <>
                      <button type="button" className="np-auth-text" onClick={() => switchMode('register')}>Back</button>
                      {' · '}Already recovering? <button type="button" className="np-auth-text" onClick={() => switchMode('login')}>Sign in</button>
                    </>
                  )}
                </p>
              </>
            )}
          </div>
        </div>

        <p className="np-muted np-auth-home">
          <Link to="/">Back to BilledTwice AI</Link>
        </p>
      </div>
    </div>
  );
}
